import { Fragment } from "react";
import { Heart, Pencil, Lightbulb, LayoutTemplate, ClipboardCheck, ArrowRight, Scale, Users, BookOpenCheck } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = [
  { label: "Empathize", Icon: Heart },
  { label: "Define", Icon: Pencil },
  { label: "Ideate", Icon: Lightbulb },
  { label: "Prototype", Icon: LayoutTemplate },
  { label: "Test", Icon: ClipboardCheck },
];

const RESEARCH_TRACKS = [
  { num: "I", title: "Policy Change", Icon: Scale, desc: "Grounded studies that inform legislation, municipal reform, and public policy at every scale." },
  { num: "II", title: "Human Rights", Icon: Users, desc: "Documentation, testimony, and rigorous evidence supporting communities under strain." },
  { num: "III", title: "Public Knowledge", Icon: BookOpenCheck, desc: "Research findings released openly, in plain language, so citizens can act on them." },
];

function ProcessGraphic() {
  return (
    <div className="w-full" data-testid="process-graphic" aria-label="Five step research process">
      <div className="hidden md:flex items-center justify-between gap-2">
        {STEPS.map((s, i) => (
          <Fragment key={s.label}>
            <div className="flex flex-col items-center gap-4 min-w-0">
              <div className="w-24 h-24 lg:w-28 lg:h-28 border border-[#111111] rounded-full flex items-center justify-center bg-white">
                <s.Icon size={32} strokeWidth={1.25} className="text-[#111111]" />
              </div>
              <div className="font-ui text-[10px] md:text-[11px] tracking-[0.25em] uppercase font-semibold text-[#111111]">{s.label}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 flex items-center justify-center -mt-8 min-w-0">
                <div className="w-full flex items-center gap-2">
                  <span className="h-px flex-1 bg-[#111111]" aria-hidden="true" />
                  <ArrowRight size={16} className="text-[#111111] shrink-0" strokeWidth={1.25} />
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>
      <div className="md:hidden flex flex-col items-center gap-4">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex flex-col items-center gap-2">
            <div className="w-20 h-20 border border-[#111111] rounded-full flex items-center justify-center bg-white">
              <s.Icon size={26} strokeWidth={1.25} className="text-[#111111]" />
            </div>
            <div className="font-ui text-[10px] tracking-[0.25em] uppercase font-semibold text-[#111111]">{s.label}</div>
            {i < STEPS.length - 1 && <div className="h-6 w-px bg-[#111111]" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function YouthLeaders() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="eyebrow mb-8">Public Research</div>
      <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight max-w-4xl">
        Research that changes policy. Research that defends rights.
      </h1>
      <p className="font-body text-lg md:text-xl text-[#3A3A3A] mt-8 max-w-3xl leading-relaxed">
        The Public Research partition invites citizens, students, and scholars to contribute to open, collaborative studies. Our findings feed directly into policy change, human rights advocacy, and public knowledge. Everyone is welcome. No credential is required to help build evidence for a fairer world.
      </p>

      {/* Research Tracks */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#DCDCDC]" data-testid="research-tracks">
        {RESEARCH_TRACKS.map((r) => (
          <div key={r.num} className="border-r border-b border-[#DCDCDC] p-8">
            <div className="flex items-baseline justify-between mb-6">
              <div className="num text-[#DCDCDC]">{r.num}</div>
              <r.Icon size={26} strokeWidth={1.25} />
            </div>
            <h3 className="font-headline text-2xl md:text-3xl leading-tight">{r.title}</h3>
            <p className="font-body text-[#3A3A3A] mt-4">{r.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/get-involved" className="btn-primary" data-testid="research-join-cta">Join a Study</Link>
        <Link to="/library" className="btn-outline">Read Ongoing Research</Link>
      </div>

      {/* Public Research Forms */}
      <div className="mt-16 border border-[#111111] p-8 md:p-12" data-testid="research-forms">
        <div className="eyebrow mb-4">Contribute to Public Research</div>
        <h2 className="font-headline text-3xl md:text-4xl leading-tight mb-4">Two ways to take part.</h2>
        <p className="font-body text-[#3A3A3A] max-w-2xl mb-8">
          Whether you have research to submit or want to participate as a citizen-contributor, the forms below open our official intake channels.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#DCDCDC] p-6">
            <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">Form I</div>
            <div className="font-headline text-2xl mt-3">Vote16 Research Participant Survey</div>
            <p className="font-body text-[#3A3A3A] mt-3">Contribute to our study on youth voting age and civic participation.</p>
            <a href="https://form.typeform.com/to/qxhyzvus" target="_blank" rel="noopener noreferrer" className="btn-primary mt-6 inline-block" data-testid="research-form-1">Take the Vote16 Survey</a>
          </div>
          <div className="border border-[#DCDCDC] p-6">
            <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-[#6C6C6C]">Form II</div>
            <div className="font-headline text-2xl mt-3">What Carries Us</div>
            <p className="font-body text-[#3A3A3A] mt-3">Share what sustains you, your community, and your work.</p>
            <a href="https://form.typeform.com/to/m9xk1zUt" target="_blank" rel="noopener noreferrer" className="btn-outline mt-6 inline-block" data-testid="research-form-2">Open the What Carries Us Form</a>
          </div>
        </div>
      </div>

      {/* Method Graphic */}
      <div className="mt-20 border border-[#DCDCDC] p-8 md:p-16 bg-white">
        <div className="eyebrow mb-8">The Research Method</div>
        <ProcessGraphic />
        <div className="mt-10 pt-8 border-t border-[#DCDCDC] text-center">
          <p className="font-headline text-xl md:text-2xl text-[#111111] italic">
            "Qualitative and quantitative research, built on positive solutions."
          </p>
        </div>
      </div>

      <hr className="hairline my-20" />

      {/* Courses moved to its own page — see /courses */}
    </div>
  );
}
